// Single source of truth. Swap any client to anonymous form by setting named:false.

export const SITE = {
    entity: "Phoebus AI Solutions Private Limited",
    email: "sales@phoebusai.com",
    phone: "+91-70759-69566",
    whatsappNumber: "917075969566",
    calendarUrl:
        "https://calendly.com/rajesh-phoebusai/consultation-with-phoebus-ai",
    hours: "9am to 6pm GST",
    address:
        "02-067/271 & 296/219, Shirdi Hills, Gajularamaram Road, Medchal Malkajgiri District, Hyderabad, Telangana, India 500055",
    linkedin:
        "https://www.linkedin.com/company/phoebus-ai-solutions-private-limited/",
    facebook: "https://www.facebook.com/profile.php?id=61552613999399",
    positioning: "AI systems that run inside your business, not on top of it.",
};

export function waLink(text) {
    return `https://wa.me/${SITE.whatsappNumber}?text=${encodeURIComponent(text)}`;
}

export const NAV = [
    { label: "AI Agents", to: "/ai-agents" },
    { label: "Conversational AI", to: "/conversational-ai" },
    { label: "Work", to: "/work" },
    { label: "About", to: "/about" },
];

export const PRINCIPLES = [
    {
        n: "01",
        title: "Process-first, not code-first",
        body: "Every engagement starts by mapping how your team actually works today, before a line of code is written.",
    },
    {
        n: "02",
        title: "Purpose-built, not off-the-shelf",
        body: "Smaller, purpose-trained models around your domain and data — deployable in your own environment.",
    },
    {
        n: "03",
        title: "One team, start to finish",
        body: "The team that scopes an engagement designs, builds, and deploys it. No handoff to a separate delivery bench.",
    },
];

export const STAGES = [
    {
        n: "01",
        title: "Discovery & scoping",
        duration: "1–2 weeks",
        body: "Mapping current workflows, defining the automation opportunity.",
    },
    {
        n: "02",
        title: "Pilot deployment",
        duration: "6–8 weeks",
        body: "Fixed-scope, fixed-timeline build to prove value fast.",
    },
    {
        n: "03",
        title: "Scale & retain",
        duration: "Ongoing",
        body: "Expanding scope, maintaining the system as a long-term partner.",
    },
];

export const CAPABILITIES = [
    {
        title: "Autonomous agents",
        body: "Agents that classify, extract, decide, and act on unstructured inbound work.",
    },
    {
        title: "Workflow automation",
        body: "Automation across email, messaging, and internal systems.",
    },
    {
        title: "System integration",
        body: "Integration with CRM, ERP, ticketing, and line-of-business tools via API.",
    },
    {
        title: "Private / VPC deployment",
        body: "Deployment inside your own environment, with monitoring and analytics.",
    },
    {
        title: "Conventional software",
        body: "Conventional software development wherever the workflow needs it.",
    },
];

export const ROI = [
    {
        today: "Inbound work arrives unstructured across email and messaging",
        absorbs: "Reads it, classifies intent, extracts the fields a human would have to retype",
        changes: "Handling time stops scaling linearly with volume",
    },
    {
        today: "A person re-keys details into the CRM or ticketing system",
        absorbs: "Writes to those systems directly via API",
        changes: "The re-keying step and its error rate disappear",
    },
    {
        today: "Response quality varies by who picks it up and when",
        absorbs: "Applies the same logic on every item, at any hour",
        changes: "Consistency becomes a property of the system, not of staffing",
    },
    {
        today: "Volume spikes are absorbed by overtime or headcount",
        absorbs: "Absorbs volume without a proportional cost increase",
        changes: "Peak capacity is decoupled from headcount",
    },
];

export const PLATFORMS = [
    "Yellow.ai",
    "Spurnow",
    "SAP",
    "Azure AD",
    "Salesforce",
    "MS Dynamics CRM",
    "Hospital Information Systems",
    "Shopify",
    "WhatsApp Commerce",
    "MS Teams",
    "Confluence",
];

export const TESTIMONIALS = [
    {
        quote: "…their exceptional work in developing our AI-driven HR virtual assistant… The virtual assistant has significantly streamlined our HR processes and improved employee engagement… We highly recommend Phoebus AI for any AI and technology development projects.",
        name: "Mallari Harapanahalli",
        role: "HR Technology Lead, Titan Company Limited",
    },
    {
        quote: "…deeply committed to customer satisfaction, effortlessly handling our requirements and enhancements with finesse… we've not only developed a cutting-edge tool but also transformed how we engage with our co-living community.",
        name: "Chirag Boonlia",
        role: "Chief Technology Officer, Embassy Property Developments Private Limited",
    },
];

// display -> shown name; set named:false to render `anonymous` instead.
export const CLIENTS = [
    {
        id: "eagle-hills",
        slug: "rigawaterfront-eagle-hills",
        display: "Eagle Hills",
        anonymous: "A master developer in Abu Dhabi",
        named: true,
        featured: true,
        project: "Rigawaterfront",
        region: "Abu Dhabi, UAE",
        sector: "Real Estate",
        duration: "Ongoing",
        channels: ["WhatsApp", "Web"],
        integrations: ["Microsoft Dynamics CRM"],
        summary:
            "An AI-powered conversational platform across WhatsApp and Web, integrated with Microsoft Dynamics CRM, automating the full customer journey from first enquiry to post-visit follow-up.",
        image: "https://images.unsplash.com/photo-1607414851776-f2fcc379fb48?auto=format&fit=crop&w=1600&q=70",
        imageAlt:
            "Waterfront real-estate development at dusk, representing the Rigawaterfront project in Abu Dhabi",
        problem:
            "A high-value property launch generates enquiries across many channels. Manually capturing leads, scheduling site visits, issuing visitor passes, and following up consistently does not scale — and a slow or inconsistent response costs a sale.",
        solution:
            "A conversational platform across WhatsApp and Web, integrated with Microsoft Dynamics CRM. It automates lead capture, interest registration, site-visit scheduling, visitor-pass notifications, live-agent handover, post-visit feedback, and personalised communications. GenAI powers intelligent FAQs, contextual assistance, and proactive campaigns.",
        impact: "Unified customer engagement, streamlined sales operations, reduced manual effort, and a better property-buying experience across every touchpoint.",
    },
    {
        id: "qatar-post",
        slug: "qatar-post",
        display: "Qatar Post",
        anonymous: "A national postal operator in the GCC",
        named: false,
        featured: true,
        region: "Qatar",
        sector: "Public Sector",
        duration: "—",
        summary:
            "A regional public-sector conversational deployment for a national postal operator.",
        image: "https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&w=1600&q=70",
        imageAlt:
            "Large-scale logistics distribution warehouse, representing national postal and parcel operations",
        problem:
            "Public-sector service channels handle large, varied query volumes where consistency and availability matter.",
        solution:
            "A conversational deployment tailored to a national postal operator's service workflows.",
        impact: "A regional public-sector reference for Phoebus AI's GCC delivery experience.",
    },
    {
        id: "manipal",
        slug: "manipal-hospitals",
        display: "Manipal Hospitals",
        anonymous: "A large private hospital group",
        named: false,
        region: "India",
        sector: "Healthcare",
        duration: "3 months",
        summary:
            "A booking and lead-generation bot integrated with the hospital's HIS, with live-agent handoff.",
        problem:
            "An existing bot saw low adoption and gave limited visibility into the leads it produced.",
        solution:
            "A booking and lead-gen bot integrated with the hospital information system (HIS), with live-agent handoff.",
        impact: "Higher adoption than the previous bot and improved lead visibility.",
    },
    {
        id: "titan",
        slug: "titan-hr-assistant",
        display: "Titan Company Limited",
        anonymous: "A large consumer enterprise",
        named: true,
        region: "India",
        sector: "Enterprise HR",
        duration: "6 months",
        summary:
            "An HR virtual assistant across MS Teams, WhatsApp and web, integrated with SAP and Azure AD.",
        problem:
            "HR fielded repetitive queries — leave, payroll, policy, approvals, asset tracking — across a large workforce.",
        solution:
            "An HR virtual assistant across MS Teams, WhatsApp and web, integrated with SAP and Azure AD, covering leave, payroll, policy queries, approvals, and asset tracking.",
        impact: "Streamlined HR processes and measurably improved employee engagement.",
        testimonial: 0,
    },
    {
        id: "olive-embassy",
        slug: "olive-embassy",
        display: "Olive · Embassy Property Developments",
        anonymous: "A co-living platform operator",
        named: true,
        region: "India",
        sector: "Real Estate",
        duration: "Ongoing",
        summary:
            "A GPT-enabled bot for Embassy's Olive co-living platform.",
        problem:
            "A co-living community needed a responsive, always-on way to engage residents and prospects.",
        solution: "A GPT-enabled conversational bot for the Olive co-living platform.",
        impact: "Transformed how the team engages with its co-living community.",
        testimonial: 1,
    },
    {
        id: "college-ave",
        slug: "college-ave-voice",
        display: "College Ave",
        anonymous: "A US student-lending fintech",
        named: false,
        region: "USA",
        sector: "Fintech",
        duration: "—",
        summary:
            "A voice bot handling high borrower query volumes on loan status and eligibility, end to end.",
        problem:
            "High volumes of borrower calls on loan status and eligibility strained support capacity.",
        solution:
            "A voice bot that handles borrower queries on loan status and eligibility with end-to-end voice resolution.",
        impact: "End-to-end voice resolution at high query volumes.",
    },
    {
        id: "b2b-kb",
        slug: "b2b-knowledge-base",
        display: "B2B Knowledge-Base Support",
        anonymous: "An enterprise SaaS vendor",
        named: false,
        region: "—",
        sector: "Enterprise SaaS",
        duration: "5 weeks",
        summary:
            "A knowledge-base bot integrated with Salesforce/Confluence and CRM.",
        problem:
            "Repetitive support requests consumed engineering and support time.",
        solution:
            "A knowledge-base bot integrated with Salesforce, Confluence, and CRM.",
        impact: "A significant drop in support requests from week one.",
    },
    {
        id: "b2b-whatsapp",
        slug: "whatsapp-commerce",
        display: "B2B WhatsApp Commerce",
        anonymous: "An FMCG distributor",
        named: false,
        region: "Multi-region",
        sector: "FMCG",
        duration: "3 weeks",
        summary: "A WhatsApp engagement bot with Shopify integration.",
        problem:
            "Multi-region B2B engagement needed a low-friction channel that fit how buyers already communicate.",
        solution:
            "A WhatsApp engagement bot with Shopify integration, deployed across multiple regions.",
        impact: "Increased qualified leads across regions.",
    },
    {
        id: "loyalty",
        slug: "loyalty-support-automation",
        display: "Loyalty & Support Automation",
        anonymous: "A retail & hypermarket group",
        named: false,
        region: "Multi-region",
        sector: "Retail & Hypermarkets",
        duration: "—",
        summary:
            "Loyalty points, coupon redemption, and support at scale with custom CRM integration.",
        problem:
            "Loyalty and support queries at hypermarket scale created heavy ticket volume.",
        solution:
            "Loyalty points, coupon redemption, and support at scale, with custom CRM integration.",
        impact: "Fewer tickets and a growing lead pipeline.",
    },
    {
        id: "social-commerce",
        slug: "social-commerce-automation",
        display: "Social Commerce Automation",
        anonymous: "An e-commerce brand",
        named: false,
        region: "—",
        sector: "E-commerce",
        duration: "—",
        summary:
            "Instagram and Facebook comment handling with WhatsApp and web workflow automation.",
        problem:
            "Social comments and messages across platforms were slow and inconsistent to handle.",
        solution:
            "Instagram and Facebook comment handling, WhatsApp and web workflow automation, with Shopify integration.",
        impact: "Consistent, automated engagement across social and messaging channels.",
    },
];

export function clientName(c) {
    return c.named ? c.display : c.anonymous;
}

export function getClient(slug) {
    return CLIENTS.find((c) => c.slug === slug);
}
